import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

import { HeaderComponent } from '@shared/components/header/header.component';
import { FooterActionComponent } from '@shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { AlertCardComponent } from '@shared/components/alert-card/alert-card.component';
import { PageScrollSpyComponent, ScrollSpySection } from '@shared/components/page-scroll-spy/page-scroll-spy.component';

import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { TipoDocumentoImpressaoEnum } from '@shared/enums/vendas/tipo-documento-impressao.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ConfigSeparacaoService } from '@shared/services/apis/vendas/config-separacao.service';
import { ConfigVendasGeralService } from '@shared/services/apis/vendas/config-vendas-geral.service';
import { AutomacaoVendaService } from '@shared/services/apis/vendas/automacao-venda.service';
import { ReportLookupService } from '@shared/services/apis/report/report-lookup.service';
import { ConfigSeparacaoRequest } from '@shared/services/requests/vendas/config-separacao.request';
import { TipoLayoutDanfe } from '@shared/services/models/report/tipo-layout-danfe.model';
import { TipoSeparadorImpressaoTermica } from '@shared/services/models/report/tipo-separador-impressao-termica.model';
import { TipoDocumentoImpressao } from '@shared/services/responses/vendas/forma-envio-lookups.response';

@Component({
  selector: 'app-config-separacao',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatRadioModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    HeaderComponent,
    FooterActionComponent,
    LoadingBoxComponent,
    AlertCardComponent,
    PageScrollSpyComponent,
  ],
  templateUrl: './config-separacao.component.html',
  styleUrls: ['./config-separacao.component.css'],
})
export class ConfigSeparacaoComponent implements OnInit {
  public moduloEnum = ModuloEnum.Configuracoes;
  public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Vendas;
  public form: FormGroup;
  public isLoading = false;
  public isSaving = false;

  private idConfigSeparacao = 0;

  // Só libera o Salvar depois que a configuração da empresa realmente chegou: salvar com o carregamento
  // falho gravaria os defaults do formulário sobre o que está no banco (mesmo cuidado de config-pedido-venda).
  public configCarregada = false;

  // Dependências vindas de outras telas de Vendas.
  public isTrabalhaComSeparacao = true;
  public isTrabalhaComExpedicao = false;
  public isAutomacaoAutorizacaoNfeAtiva = true;

  public opcoesTipoDocumentoImpressao: TipoDocumentoImpressao[] = [];
  public opcoesTipoLayoutDanfe: TipoLayoutDanfe[] = [];
  public opcoesTipoSeparadorImpressaoTermica: TipoSeparadorImpressaoTermica[] = [];

  public sections: ScrollSpySection[] = [
    { id: 'exibicao-conferencia', label: 'Exibição e conferência' },
    { id: 'regras-separacao', label: 'Regras da separação' },
    { id: 'autorizacao-nota-fiscal', label: 'Autorização de nota fiscal' },
    { id: 'impressao', label: 'Impressão' },
  ];

  // Origem da navegação: 'gerais' quando aberta pelo atalho de Configurações Gerais de Vendas; senão o menu.
  private origem: string | null = null;
  public tooltipVoltar = 'Voltar para Configurações de Vendas';

  constructor(
    private formBuilder: FormBuilder,
    private configSeparacaoService: ConfigSeparacaoService,
    private configVendasGeralService: ConfigVendasGeralService,
    private automacaoVendaService: AutomacaoVendaService,
    private reportLookupService: ReportLookupService,
    private alertService: AlertService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.form = this.formBuilder.group({
      // Exibição e conferência
      isImprimirObservacaoInternaVenda: [false],
      isExibirImagemProduto: [false],
      isExibirCodigoBarrasGtin: [false],
      isImprimirCodigoBarrasVenda: [false],
      isImprimirEstoqueFisico: [false],
      isImprimirEstoqueDisponivel: [false],
      // Regras da separação
      isConfirmarNumeroVolumes: [false],
      isOcultarAdicaoManualItem: [false],
      isReceberMarcadoresOrigem: [false],
      isImpedirSeparacaoSimultanea: [false],
      // Autorização de nota fiscal
      isPermitirAutorizacaoNfeSeparacao: [false],
      isPermitirAutorizacaoNfeEmbalagem: [false],
      isPermitirExpedicaoInicioEmbalagem: [false],
      // Impressão
      listTipoDocumentoImpressao: [[] as number[]],
      idTipoLayoutDanfe: [1],
      idTipoSeparadorImpressaoTermica: [1],
    });
  }

  ngOnInit(): void {
    this.origem = this.route.snapshot.queryParamMap.get('origem');
    if (this.origem === 'gerais') this.tooltipVoltar = 'Voltar para Configurações Gerais de Vendas';
    this.carregarDados();
  }

  private carregarDados(): void {
    this.isLoading = true;
    forkJoin({
      config: this.configSeparacaoService.get().pipe(catchError(() => of(null))),
      lookups: this.configSeparacaoService.getLookups().pipe(catchError(() => of(null))),
      geral: this.configVendasGeralService.getLookup().pipe(catchError(() => of(null))),
      automacao: this.automacaoVendaService.get().pipe(catchError(() => of(null))),
      layoutDanfe: this.reportLookupService.getTiposLayoutDanfe().pipe(catchError(() => of(null))),
      separadorImpressaoTermica: this.reportLookupService.getTiposSeparadorImpressaoTermica().pipe(catchError(() => of(null))),
    }).subscribe({
      next: res => {
        this.isLoading = false;

        if (res.lookups && !res.lookups.isError) {
          this.opcoesTipoDocumentoImpressao = res.lookups.listTipoDocumentoImpressao || [];
        } else if (res.lookups?.isError) {
          this.alertService.show('Aviso', res.lookups.errorDescription, AlertType.warning);
        }

        if (res.layoutDanfe && !res.layoutDanfe.isError) {
          this.opcoesTipoLayoutDanfe = res.layoutDanfe.listaTipoLayoutDanfe || [];
        } else if (res.layoutDanfe?.isError) {
          this.alertService.show('Aviso', res.layoutDanfe.errorDescription, AlertType.warning);
        }

        if (res.separadorImpressaoTermica && !res.separadorImpressaoTermica.isError) {
          this.opcoesTipoSeparadorImpressaoTermica = res.separadorImpressaoTermica.listaTipoSeparadorImpressaoTermica || [];
        } else if (res.separadorImpressaoTermica?.isError) {
          this.alertService.show('Aviso', res.separadorImpressaoTermica.errorDescription, AlertType.warning);
        }

        if (res.geral && !res.geral.isError && res.geral.configVendasGeral) {
          this.isTrabalhaComSeparacao = res.geral.configVendasGeral.isTrabalhaComSeparacao;
          this.isTrabalhaComExpedicao = res.geral.configVendasGeral.isTrabalhaComExpedicao;
        }

        if (res.automacao && !res.automacao.isError && res.automacao.config) {
          this.isAutomacaoAutorizacaoNfeAtiva = res.automacao.config.isAtivoAutorizacaoNFe;
        }

        if (res.config && !res.config.isError && res.config.configSeparacao) {
          const c = res.config.configSeparacao;
          this.idConfigSeparacao = c.idConfigSeparacao;
          this.form.patchValue({
            isImprimirObservacaoInternaVenda: c.isImprimirObservacaoInternaVenda,
            isExibirImagemProduto: c.isExibirImagemProduto,
            isExibirCodigoBarrasGtin: c.isExibirCodigoBarrasGtin,
            isImprimirCodigoBarrasVenda: c.isImprimirCodigoBarrasVenda,
            isImprimirEstoqueFisico: c.isImprimirEstoqueFisico,
            isImprimirEstoqueDisponivel: c.isImprimirEstoqueDisponivel,
            isConfirmarNumeroVolumes: c.isConfirmarNumeroVolumes,
            isOcultarAdicaoManualItem: c.isOcultarAdicaoManualItem,
            isReceberMarcadoresOrigem: c.isReceberMarcadoresOrigem,
            isImpedirSeparacaoSimultanea: c.isImpedirSeparacaoSimultanea,
            isPermitirAutorizacaoNfeSeparacao: c.isPermitirAutorizacaoNfeSeparacao,
            isPermitirAutorizacaoNfeEmbalagem: c.isPermitirAutorizacaoNfeEmbalagem,
            isPermitirExpedicaoInicioEmbalagem: c.isPermitirExpedicaoInicioEmbalagem,
            listTipoDocumentoImpressao: res.config.listTipoDocumentoImpressao || [],
            idTipoLayoutDanfe: c.idTipoLayoutDanfe,
            idTipoSeparadorImpressaoTermica: c.idTipoSeparadorImpressaoTermica,
          });
          this.form.markAsPristine();
          this.configCarregada = true;
        } else if (res.config?.isError) {
          this.alertService.show('Aviso', res.config.errorDescription, AlertType.warning);
        } else {
          this.alertService.show(
            'Aviso',
            'Não foi possível carregar as configurações da separação. Recarregue a tela antes de salvar.',
            AlertType.warning
          );
        }
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Aviso', 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  /** Aviso quando a autorização na separação/embalagem depende de uma automação desligada. */
  public get mostrarAvisoAutomacaoNfe(): boolean {
    return !this.isAutomacaoAutorizacaoNfeAtiva
      && (!!this.form.get('isPermitirAutorizacaoNfeSeparacao')?.value || !!this.form.get('isPermitirAutorizacaoNfeEmbalagem')?.value);
  }

  public get isDanfeSelecionada(): boolean {
    return (this.form.get('listTipoDocumentoImpressao')?.value as number[] || []).includes(TipoDocumentoImpressaoEnum.Danfe);
  }

  /** Texto do campo de impressão automática: "Não imprimir" ou os nomes selecionados separados por vírgula. */
  public get resumoImpressaoAutomatica(): string {
    const selecionados = this.form.get('listTipoDocumentoImpressao')?.value as number[] || [];
    if (selecionados.length === 0) return 'Não imprimir';
    return this.opcoesTipoDocumentoImpressao
      .filter(t => selecionados.includes(t.idTipoDocumentoImpressao))
      .map(t => t.descricao)
      .join(', ');
  }

  public salvar(): void {
    if (!this.configCarregada) {
      this.alertService.show('Atenção', 'As configurações não foram carregadas. Recarregue a tela antes de salvar.', AlertType.error);
      return;
    }

    this.isSaving = true;
    const raw = this.form.getRawValue();
    const request: ConfigSeparacaoRequest = {
      isImprimirObservacaoInternaVenda: raw.isImprimirObservacaoInternaVenda,
      isExibirImagemProduto: raw.isExibirImagemProduto,
      isExibirCodigoBarrasGtin: raw.isExibirCodigoBarrasGtin,
      isImprimirCodigoBarrasVenda: raw.isImprimirCodigoBarrasVenda,
      isImprimirEstoqueFisico: raw.isImprimirEstoqueFisico,
      isImprimirEstoqueDisponivel: raw.isImprimirEstoqueDisponivel,
      isConfirmarNumeroVolumes: raw.isConfirmarNumeroVolumes,
      isOcultarAdicaoManualItem: raw.isOcultarAdicaoManualItem,
      isReceberMarcadoresOrigem: raw.isReceberMarcadoresOrigem,
      isImpedirSeparacaoSimultanea: raw.isImpedirSeparacaoSimultanea,
      isPermitirAutorizacaoNfeSeparacao: raw.isPermitirAutorizacaoNfeSeparacao,
      isPermitirAutorizacaoNfeEmbalagem: raw.isPermitirAutorizacaoNfeEmbalagem,
      // Enviado mesmo com a expedição desligada: o valor fica guardado para quando ela for religada.
      isPermitirExpedicaoInicioEmbalagem: raw.isPermitirExpedicaoInicioEmbalagem,
      idTipoLayoutDanfe: raw.idTipoLayoutDanfe,
      idTipoSeparadorImpressaoTermica: raw.idTipoSeparadorImpressaoTermica,
      listTipoDocumentoImpressao: raw.listTipoDocumentoImpressao || [],
    };

    this.configSeparacaoService.update(this.idConfigSeparacao, request).subscribe({
      next: response => {
        this.isSaving = false;
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.warning);
          return;
        }
        this.form.markAsPristine();
        this.alertService.show('Sucesso', 'Configurações da separação atualizadas.', AlertType.success);
      },
      error: error => {
        this.isSaving = false;
        this.alertService.show('Aviso', error || 'Falha na comunicação com o servidor.', AlertType.warning);
      },
    });
  }

  public voltar(): void {
    this.router.navigate([this.origem === 'gerais' ? '/configuracoes/vendas/gerais' : '/configuracoes/vendas']);
  }
}
